import { useEffect } from "react";
import { Routes, Route, Navigate } from "react-router-dom";
import LoginPage from "./pages/auth/LoginPage";
import ChangePasswordPage from "./pages/auth/ChangePasswordPage";
import CashPasswordPage from "./pages/auth/CashPasswordPage";
import AcceptInvitationPage from "./pages/auth/AcceptInvitationPage";
import ForgotPasswordPage from "./pages/auth/ForgotPasswordPage";
import ResetPasswordPage from "./pages/auth/ResetPasswordPage";
import DashboardPage from "./pages/owner/DashboardPage";
import SalonsPage from "./pages/salons/SalonsPage";
import StaffPage from "./pages/staff/StaffPage";
import CustomersPage from "./pages/customers/CustomersPage";
import ServicesPage from "./pages/services/ServicesPage";
import AppointmentsPage from "./pages/appointments/AppointmentsPage";
import TasksPage from "./pages/tasks/TasksPage";
import DocumentsPage from "./pages/documents/DocumentsPage";
import PayrollPage from "./pages/payroll/PayrollPage";
import InventoryPage from "./pages/inventory/InventoryPage";
import StockReportsPage from "./pages/inventory/StockReportsPage";
import CashRegisterPage from "./pages/cash/CashRegisterPage";
import FinancePage from "./pages/finance/FinancePage";
import CampaignsPage from "./pages/campaigns/CampaignsPage";
import LoyaltyPage from "./pages/loyalty/LoyaltyPage";
import ReportsPage from "./pages/reports/ReportsPage";
import SalonOnboardingPage from "./pages/admin/SalonOnboardingPage";
import PackagesPage from "./pages/admin/PackagesPage";
import SalonHierarchyPage from "./pages/admin/SalonHierarchyPage";
import SystemSettingsPage from "./pages/admin/SystemSettingsPage";
import SystemAdminsPage from "./pages/admin/SystemAdminsPage";
import SubscriptionRequestsPage from "./pages/admin/SubscriptionRequestsPage";
import DevVersionsPage from "./pages/dev/DevVersionsPage";
import DevRoadmapPage from "./pages/dev/DevRoadmapPage";
import SettingsPage from "./pages/settings/SettingsPage";
import InvoicesPage from "./pages/invoices/InvoicesPage";
import MySubscriptionPage from "./pages/owner/MySubscriptionPage";
import PrivateRoute from "./components/Auth/PrivateRoute";
import RoleRoute from "./components/Auth/RoleRoute";
import Layout from "./components/Layout/Layout";

function App() {
  useEffect(() => {
    const handleMouseDown = (e) => {
      // Only drag with left click
      if (e.button !== 0) return;

      // Find the closest modal card container
      const modalBox = e.target.closest('.bg-surface-card, .bg-white, [class*="shadow-modal"]');
      if (!modalBox) return;

      // Ensure it is inside a fixed modal overlay/backdrop
      const backdrop = modalBox.closest('.fixed.inset-0, .fixed, .z-50, .z-\\[999\\]');
      if (!backdrop) return;

      // Ensure drag starts from the header or top section of the modal box
      const isHeaderClick = e.target.closest('[class*="border-b"], h3') || 
                            (e.clientY - modalBox.getBoundingClientRect().top < 65);
      if (!isHeaderClick) return;

      // Don't drag if clicking interactive elements or icons
      if (e.target.closest('button, input, select, textarea, a, svg, .material-symbols-outlined')) return;

      e.preventDefault();

      let isDragging = true;
      const startX = e.clientX;
      const startY = e.clientY;

      // Parse current translation coordinates
      const currentDragX = parseFloat(modalBox.dataset.dragX || 0);
      const currentDragY = parseFloat(modalBox.dataset.dragY || 0);

      // Disable text selection and change cursor styles
      document.body.style.userSelect = 'none';
      const originalCursor = document.body.style.cursor;
      document.body.style.cursor = 'grabbing';
      
      const originalTransition = modalBox.style.transition;
      modalBox.style.transition = 'none';

      const handleMouseMove = (moveEvent) => {
        if (!isDragging) return;
        const dx = moveEvent.clientX - startX;
        const dy = moveEvent.clientY - startY;
        const newX = currentDragX + dx;
        const newY = currentDragY + dy;

        modalBox.style.transform = `translate(${newX}px, ${newY}px)`;
        modalBox.dataset.dragX = newX;
        modalBox.dataset.dragY = newY;
      };

      const handleMouseUp = () => {
        isDragging = false;
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
        document.body.style.userSelect = '';
        document.body.style.cursor = originalCursor;
        modalBox.style.transition = originalTransition;
      };

      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
    };

    document.addEventListener('mousedown', handleMouseDown);
    return () => {
      document.removeEventListener('mousedown', handleMouseDown);
    };
  }, []);

  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route path="/activate" element={<AcceptInvitationPage />} />
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      <Route
        path="/change-password"
        element={
          <PrivateRoute>
            <ChangePasswordPage />
          </PrivateRoute>
        }
      />

      <Route
        element={
          <PrivateRoute>
            <Layout />
          </PrivateRoute>
        }
      >
        <Route path="/" element={<Navigate to="/dashboard" replace />} />
        <Route path="/dashboard" element={<DashboardPage />} />
        <Route path="/settings" element={<SettingsPage />} />
        <Route
          path="/subscription"
          element={
            <RoleRoute roles={["MANAGER"]}>
              <MySubscriptionPage />
            </RoleRoute>
          }
        />
        <Route
          path="/cash-password"
          element={
            <RoleRoute roles={["MANAGER"]}>
              <CashPasswordPage />
            </RoleRoute>
          }
        />
        <Route
          path="/admin/subscription-requests"
          element={
            <RoleRoute roles={["ADMIN"]}>
              <SubscriptionRequestsPage />
            </RoleRoute>
          }
        />
        <Route
          path="/salons"
          element={
            <RoleRoute roles={["ADMIN", "MANAGER"]}>
              <SalonsPage />
            </RoleRoute>
          }
        />
        <Route
          path="/staff"
          element={
            <RoleRoute roles={["ADMIN", "MANAGER"]}>
              <StaffPage />
            </RoleRoute>
          }
        />
        <Route
          path="/customers"
          element={
            <RoleRoute roles={["MANAGER", "STAFF"]}>
              <CustomersPage />
            </RoleRoute>
          }
        />
        <Route
          path="/services"
          element={
            <RoleRoute roles={["MANAGER", "STAFF"]}>
              <ServicesPage />
            </RoleRoute>
          }
        />
        <Route
          path="/appointments"
          element={
            <RoleRoute roles={["MANAGER", "STAFF"]}>
              <AppointmentsPage />
            </RoleRoute>
          }
        />
        <Route
          path="/tasks"
          element={
            <RoleRoute roles={["MANAGER", "STAFF"]}>
              <TasksPage />
            </RoleRoute>
          }
        />
        <Route
          path="/documents"
          element={
            <RoleRoute roles={["ADMIN", "MANAGER", "STAFF"]}>
              <DocumentsPage />
            </RoleRoute>
          }
        />
        <Route
          path="/payroll"
          element={
            <RoleRoute roles={["MANAGER"]}>
              <PayrollPage />
            </RoleRoute>
          }
        />
        <Route
          path="/inventory"
          element={
            <RoleRoute roles={["MANAGER", "STAFF"]}>
              <InventoryPage />
            </RoleRoute>
          }
        />
        <Route
          path="/inventory/reports"
          element={
            <RoleRoute roles={["MANAGER"]}>
              <StockReportsPage />
            </RoleRoute>
          }
        />
        <Route
          path="/cash"
          element={
            <RoleRoute roles={["MANAGER", "STAFF"]}>
              <CashRegisterPage />
            </RoleRoute>
          }
        />
        <Route
          path="/finance"
          element={
            <RoleRoute roles={["MANAGER"]}>
              <FinancePage />
            </RoleRoute>
          }
        />
        <Route
          path="/campaigns"
          element={
            <RoleRoute roles={["MANAGER"]}>
              <CampaignsPage />
            </RoleRoute>
          }
        />
        <Route
          path="/loyalty"
          element={
            <RoleRoute roles={["MANAGER"]}>
              <LoyaltyPage />
            </RoleRoute>
          }
        />
        <Route
          path="/reports"
          element={
            <RoleRoute roles={["ADMIN", "MANAGER", "STAFF"]}>
              <ReportsPage />
            </RoleRoute>
          }
        />
        <Route
          path="/invoices"
          element={
            <RoleRoute roles={["MANAGER", "STAFF"]}>
              <InvoicesPage />
            </RoleRoute>
          }
        />
        <Route
          path="/admin/onboarding"
          element={
            <RoleRoute roles={["ADMIN"]}>
              <SalonOnboardingPage />
            </RoleRoute>
          }
        />
        <Route
          path="/admin/packages"
          element={
            <RoleRoute roles={["ADMIN"]}>
              <PackagesPage />
            </RoleRoute>
          }
        />
        <Route
          path="/admin/hierarchy"
          element={
            <RoleRoute roles={["ADMIN"]}>
              <SalonHierarchyPage />
            </RoleRoute>
          }
        />
        <Route
          path="/admin/settings"
          element={
            <RoleRoute roles={["ADMIN"]}>
              <SystemSettingsPage />
            </RoleRoute>
          }
        />
        <Route
          path="/admin/admins"
          element={
            <RoleRoute roles={["ADMIN"]}>
              <SystemAdminsPage />
            </RoleRoute>
          }
        />
        <Route
          path="/dev-notes/versions"
          element={
            <RoleRoute roles={["ADMIN", "MANAGER", "STAFF"]}>
              <DevVersionsPage />
            </RoleRoute>
          }
        />
        <Route
          path="/dev-notes/roadmap"
          element={
            <RoleRoute roles={["ADMIN", "MANAGER", "STAFF"]}>
              <DevRoadmapPage />
            </RoleRoute>
          }
        />
      </Route>

      <Route path="*" element={<Navigate to="/dashboard" replace />} />
    </Routes>
  );
}

export default App;
